<template>
    <div v-if="showSetupPopup" class="order-setup-popup-container">
        <modal
            ref="modal"
            id="orderSetupPopup"
            size="modal-lg"
            :title="$t('orderPopup.modalTitle')"
            :centered="true"
            :centeredHeader="true"
            :scrollable="true"
            :show="modal.show"
            :close="modal.hide"
            :closeable="modal.closeable"
            @modal-opening="onModalOpening"
            @modal-visible="onResize"
            @modal-close="onModalClosing"
        >
            <form
                method="post"
                action=""
                enctype="multipart/form-data"
                @submit="onFormSubmit($event)"
                class="order-setup-popup-form"
                :style="formStyle"
            >
                <div ref="modalBody" class="modal-body">
                    <div class="row">
                        <div
                            v-if="response.show && typeof response.message === 'string' && response.message.length > 0"
                            class="col-12"
                        >
                            <div
                                class="alert"
                                :class="{
                                    'alert-success': response.success,
                                    'alert-danger': !response.success,
                                }"
                            >
                                <p class="mb-0">{{ response.message }}</p>
                            </div>
                        </div>

                        <div v-if="!licensePlate.showForm" class="col-12">
                            <div class="row">
                                <div class="col-12 col-md-9 horizontal-centered">
                                    <p v-if="modalText !== null" v-html="modalText"></p>
                                </div>
                            </div>

                            <!--
							In the case that the license plate country contains a template we can use
							a slightly adjusted license plate input component to display the current
							license plate in a nice way. Otherwise, we will show something like a table
							to inform the user about the entered license plate configuration.
							-->
                            <div class="row">
                                <div class="col-12 col-md-9 horizontal-centered">
                                    <license-plate-input
                                        id="orderSetupPopupLicenseDisplay"
                                        name="license_display"
                                        :required="false"
                                        :is-invalid="false"
                                        :disabled="true"
                                        :license-plate-country="selectedLicensePlateCountry"
                                        :template="selectedLicensePlateCountry.template"
                                        :value="licensePlate.value"
                                    ></license-plate-input>
                                </div>
                                <div class="col-12 col-md-9 horizontal-centered">
                                    <button
                                        type="button"
                                        class="btn btn-block btn-outline-primary mt-4"
                                        :class="{ 'mt-2': selectedLicensePlateCountryHasTemplate }"
                                        :disabled="runningRequest"
                                        @click="onShowLicensePlateChange($event)"
                                        v-html="$t('orderPopup.showLicensePlateForm')"
                                    ></button>
                                </div>
                            </div>

                            <div v-if="this.config.automaticPopupConfirmationActive" class="row">
                                <div class="col-12 col-md-9 horizontal-centered">
                                    <div class="align-items-center mt-4">
                                        <div
                                            class="mt-1 d-block"
                                            v-html="$t('shop.automaticPopupConfirmationNotice')"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="licensePlate.showForm"
                            class="col-12"
                            :class="{ 'contains-license-plate-template': hasTemplateForm }"
                        >
                            <div class="row">
                                <div class="col-12 mb-3">
                                    <label
                                        for="orderSetupPopupLicenseCountry"
                                        class="col-form-label"
                                        v-html="$t('shop.licensing_state')"
                                    ></label>
                                    <select
                                        id="orderSetupPopupLicenseCountry"
                                        class="form-control height-auto-override"
                                        name="country_code"
                                        :class="{ 'is-invalid': formErrors.country_code }"
                                        v-model="licensePlate.country"
                                        :disabled="runningRequest"
                                        @change="onInputChange('country_code')"
                                        required
                                    >
                                        <option :value="false" disabled>{{ $t('shop.dropdown_required') }}</option>
                                        <option
                                            v-for="licensePlateCountry in licensePlateCountries"
                                            :value="licensePlateCountry.country_code"
                                            :key="licensePlateCountry.country_code"
                                        >
                                            {{ $t(`country.${licensePlateCountry.country_code}`) }}
                                        </option>
                                    </select>

                                    <div class="invalid-feedback">
                                        <span class="d-block" v-for="error in formErrors.country_code" :key="error">{{
                                            error
                                        }}</span>
                                    </div>
                                </div>

                                <div v-if="selectedLicensePlateCountry !== null" class="col-12 col-md-9">
                                    <label
                                        for="orderSetupPopupLicensePlate"
                                        class="col-form-label"
                                        v-html="$t('shop.license_plate')"
                                    ></label>
                                    <license-plate-input
                                        id="orderSetupPopupLicensePlate"
                                        name="license"
                                        v-model="licensePlate.value"
                                        :license-plate-country="selectedLicensePlateCountry"
                                        :template="selectedLicensePlateCountry.template"
                                        :required="true"
                                        :disabled="runningRequest"
                                        :is-invalid="
                                            typeof formErrors.license === 'object' && formErrors.license.length > 0
                                        "
                                        :updated="licensePlate.updated"
                                        :special-license-plate-error="
                                            formErrors.special_license_plate_confirmed || null
                                        "
                                        @update:modelValue="onInputChange('license', $event)"
                                        @has-license-plate-template="hasTemplateForm = $event"
                                        @model-updated="licensePlate.updated = true"
                                        @is-special-changed="onSpecialLicensePlateChange"
                                    >
                                        <span class="d-block" v-for="error in formErrors.license" :key="error">{{
                                            error
                                        }}</span>
                                    </license-plate-input>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="licensePlate.showForm" class="modal-footer justify-content-center bg-light">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="onAbortLicensePlateChange($event)"
                        v-html="$t('generic.cancel')"
                        :disabled="runningRequest"
                    ></button>
                    <button type="submit" class="btn btn-primary" :disabled="runningRequest">
                        <i v-if="runningRequest" class="mdi mdi-loading mdi-spin"></i>
                        <span v-else v-html="$t('orderPopup.updateLicensePlate')"></span>
                    </button>
                </div>
                <div v-else class="modal-footer bg-light justify-content-center">
                    <button type="submit" class="btn btn-primary" :disabled="runningRequest">
                        <i v-if="runningRequest" class="mdi mdi-loading mdi-spin"></i>
                        <span v-else-if="isInsuranceOrder" v-html="$t('orderPopup.confirmLicensePlate')"></span>
                        <span v-else v-html="$t('orderPopup.confirm')"></span>
                    </button>
                </div>
            </form>
        </modal>
    </div>
</template>

<script>
export default {
    props: {
        config: {
            type: Object,
            required: true,
            default() {
                return {
                    enabled: false,
                    show: false,
                    updateUri: null,
                    confirmPopupUri: null,
                    automaticPopupConfirmationActive: false,
                };
            },
        },
        order: {
            type: Object,
            required: true,
        },
        licensePlateCountries: {
            type: Array,
            required: true,
        },
        defaultLicensePlateCountry: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            modal: {
                show: false,
                hide: false,
                closeable: false,
            },
            formStyle: {
                height: '100%',
            },
            confirmError: false,
            confirm: false,
            licensePlate: {
                showForm: false,
                country: this.defaultLicensePlateCountry,
                value: this.order.license,
                updated: false,
            },
            formErrors: {},
            changedInputs: {
                country_code: false,
                license: false,
                special_license_plate_confirmed: false,
            },
            hasTemplateForm: false,
            runningRequest: false,
            response: {
                show: false,
                success: false,
                message: null,
            },
        };
    },

    computed: {
        /**
         * Determine if the setup popup of order is enabled.
         *
         * @returns {Boolean}
         */
        isEnabled() {
            return this.config.enabled;
        },

        /**
         * Determine if the setup popup of order should be visible.
         *
         * @returns {Boolean}
         */
        shouldBeVisible() {
            return this.config.show;
        },

        /**
         * Determine if the setup popup should be visible for current user.
         *
         * @returns {Boolean}
         */
        showSetupPopup() {
            return this.isEnabled && this.shouldBeVisible;
        },

        /**
         * Search the selected license plate country in the provided option list.
         *
         * @returns {{template:null}|null}
         * */
        selectedLicensePlateCountry() {
            try {
                return this.licensePlateCountries.filter((country) => {
                    return country.country_code === this.licensePlate.country;
                })[0];
            } catch (error) {
                return null;
            }
        },

        /**
         * Determine if the selected license plate country has a template for input.
         *
         * @returns {Boolean}
         * */
        selectedLicensePlateCountryHasTemplate() {
            return (
                this.selectedLicensePlateCountry !== null &&
                typeof this.selectedLicensePlateCountry.template === 'object' &&
                this.selectedLicensePlateCountry.template !== null
            );
        },

        /**
         * Load the modal text if defined.
         *
         * @returns {String|null}
         * */
        modalText() {
            const text = this.$t('orderPopup.modalText');

            return text.trim().length > 0 ? text : null;
        },

        /**
         * @returns {boolean}
         */
        isInsuranceOrder() {
            return typeof this.order.order_insurance === 'boolean' && this.order.order_insurance;
        },
    },

    methods: {
        /**
         * Handle the visibility update of the modal.
         *
         * @returns {this}
         */
        showModal() {
            this.modal.show = true;
            this.modal.hide = false;
            this.modal.closeable = false;

            return this;
        },

        /**
         * Handle the event callback of opening modal.
         *
         * @returns {void}
         */
        onModalOpening() {
            this.$el.ownerDocument.body.classList.add('overflow-hidden');
        },

        /**
         * Handle the event callback for closing modal.
         *
         * @returns {void}
         */
        onModalClosing() {
            this.$el.ownerDocument.body.classList.remove('overflow-hidden');
        },

        /**
         * Handle the flag update of input to keep tracked of changed inputs.
         *
         * @param {String} key
         *
         * @returns {Promise<void>}
         */
        async onInputChange(key) {
            // Update the changed status only if the key is known by the default definition.
            if (typeof this.changedInputs[key] !== 'undefined') {
                this.changedInputs[key] = true;
            }
        },

        /**
         * Handle the submit callback of form to send the expected request.
         *
         * @param {SubmitEvent} $event
         *
         * @returns {void}
         */
        onFormSubmit($event) {
            $event.preventDefault();

            // In the case the form was already submitted, we have to prevent another submit.
            if (this.runningRequest) {
                return;
            }

            // Update the is submitting flag and reset the response alert.
            this.runningRequest = true;
            this.formErrors = {};
            this.response.show = false;
            this.response.success = false;
            this.response.message = null;
            this.confirmError = false;

            // Prepare the request
            const formData = new FormData();
            const uri = this.licensePlate.showForm ? this.config.updateUri : this.config.confirmPopupUri;

            if (this.licensePlate.showForm) {
                formData.append('country_code', this.licensePlate.country);
                formData.append('license', this.licensePlate.value);
                formData.append('license_confirmation', this.licensePlate.value);
                formData.append('is_special_license', this.changedInputs.special_license_plate_confirmed ? 1 : 0);
            }

            axios
                .post(uri, formData)
                .then((response) => {
                    // In the case the request was failed for some reason, we have to apply
                    // the message to our response property to show the user more details why
                    // the request was failed.
                    this.response.success = response.data.success;
                    this.response.message = response.data.message;

                    if (!this.response.success) {
                        // In the case that the response contains more details about the exact reason
                        // of failure, we have to provide them by adding these to the formErrors property.
                        if (typeof response.data.formErrors === 'object') {
                            // Step through the keys to check if the input of those was changed or not.
                            for (const key in response.data.formErrors) {
                                if (typeof this.changedInputs[key] === 'undefined' || !this.changedInputs[key]) {
                                    delete response.data.formErrors[key];
                                }
                            }

                            // After the check above, we can assign the errors to the property.
                            this.formErrors = response.data.formErrors;
                        }

                        // Finally, disable the request blocking and show the response message in
                        // an alert container.
                        this.response.show = true;
                        this.runningRequest = false;

                        return;
                    }

                    // Show the success message and reload the page after 2 seconds, to assure
                    // we will show an updated page view with all necessary data.
                    this.response.show = true;

                    setTimeout(() => {
                        window.location.href = location.href;
                    }, 2000);
                })
                .catch(() => {
                    this.runningRequest = false;
                });
        },

        /**
         * Handle the show of license plate change form.
         *
         * @param {PointerEvent} $event
         *
         * @returns {void}
         */
        onShowLicensePlateChange($event) {
            $event.preventDefault();

            if (this.sendingRequest) {
                return;
            }

            this.licensePlate.showForm = true;
            this.confirm = false;
            this.$nextTick(() => {
                this.onResize();
            });
        },

        /**
         * Handle the hide of license plate change form.
         *
         * @param {PointerEvent} $event
         *
         * @returns {void}
         */
        onAbortLicensePlateChange($event) {
            $event.preventDefault();

            if (this.sendingRequest) {
                return;
            }

            this.licensePlate.showForm = false;
            this.licensePlate.country = this.order.country_code;
            this.licensePlate.value = this.order.license;
            this.$nextTick(() => {
                this.onResize();
            });
        },

        /**
         * Handle the callback of resize event, to assure the header size will be tracked correctly.
         *
         * @returns {void}
         */
        onResize() {
            // In the case that setup popup isn't visible, we don't have to handle the update.
            if (!this.showSetupPopup) {
                return;
            }

            // Determine the height of the modal title container, to assure
            // we apply the correct value for the form container.
            const titleHeight = this.$refs.modal.$el.querySelector('.modal-header').clientHeight;

            this.formStyle.height = `calc(100% - ${titleHeight}px)`;
        },

        /**
         * @param {boolean} isSpecial
         * @returns {void}
         */
        onSpecialLicensePlateChange(isSpecial) {
            this.changedInputs.special_license_plate_confirmed = isSpecial;
            this.order.is_special_license = isSpecial;
        },
    },

    beforeUnmount() {
        window.removeEventListener('resize', () => {
            this.onResize();
        });
    },

    beforeMount() {
        window.addEventListener('resize', () => {
            this.onResize();
        });
    },

    mounted() {
        this.showModal();
    },
};
</script>
<style lang="scss">
.order-setup-popup-container {
    #popup_accepted {
        width: 3em;
        height: 0;

        &:before {
            width: 3em;
            height: 3em;
        }

        &:checked:before {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    @media (min-width: 576px) and (max-width: 992px) {
        .modal-dialog {
            max-width: unset;
        }
    }

    @media screen and (min-width: 300px) {
        #popup_accepted:checked:before {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 !important;
        }

        #popup_accepted {
            width: 3em;
            height: 4em;
        }
    }

    @media screen and (max-width: 767px) {
        .modal-dialog {
            min-height: unset;
            max-height: unset;
            margin: 0;

            .modal-content {
                width: calc(100vw - 0.5rem);
            }
        }
    }

    @media screen and (min-width: 768px) {
        .horizontal-centered {
            margin: 0 auto;
        }
    }

    .modal-header {
        padding-bottom: 0.5rem;
    }

    .order-setup-popup-form {
        position: relative;
        height: 100%;

        .modal-body {
            padding-top: 0;
        }

        .modal-footer {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;

            &.border-primary {
                border-left: 0 !important;
                border-right: 0 !important;
                border-bottom: 0 !important;
            }
        }
    }
}
</style>
